import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserPermissions, PermissionLevel } from '../types/permissions';
import permissionServiceAPI from '../api/permissionService';

// 权限上下文接口
interface PermissionContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string, level?: PermissionLevel) => boolean;
  canViewSensitiveInfo: () => boolean;
  canViewFinancialInfo: () => boolean;
  canViewStatistics: () => boolean;
  getStatisticsLevel: () => PermissionLevel;
  refreshPermissions: () => Promise<void>;
  isAdmin: () => boolean;
  isHQStaff: () => boolean;
  isWarehouseSupervisor: () => boolean;
  isWarehouseStaff: () => boolean;
  isMainAccount: () => boolean;
  isSubAccount: () => boolean;
  hiddenFields: (resourceKey: string) => string[];
  exportHiddenFields: (resourceKey: string) => string[];
}

// 创建权限上下文
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// 权限提供者组件
interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 获取用户权限信息
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const userPermissions = await permissionServiceAPI.getUserPermissions();
      setPermissions(userPermissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取权限信息失败');
      console.error('获取权限信息失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 刷新权限信息
  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  // 检查是否有特定权限
  const hasPermission = (permission: string, level?: PermissionLevel): boolean => {
    if (!permissions || !permissions.is_authenticated) {
      return false;
    }

    // 管理员拥有所有权限
    if (permissions.role === 'admin') {
      return true;
    }

    // 根据权限类型检查
    switch (permission) {
      case 'view_sensitive_info':
        return permissions.can_view_sensitive_info;
      case 'view_financial_info':
        return permissions.can_view_financial_info;
      case 'view_statistics':
        return permissions.can_view_statistics;
      default:
        // 新增：通用动作键支持，例如 'project:create'
        if (permissions.allowed_actions && permissions.allowed_actions.includes(permission)) {
          return true;
        }
        // 根据权限级别检查（保留原逻辑兼容）
        if (level) {
          const userLevel = permissions.statistics_level;
          const levelHierarchy: PermissionLevel[] = ['none', 'project', 'warehouse', 'global'];
          const userLevelIndex = levelHierarchy.indexOf(userLevel);
          const requiredLevelIndex = levelHierarchy.indexOf(level);
          return userLevelIndex >= requiredLevelIndex;
        }
        return false;
    }
  };

  // 检查是否可以查看敏感信息
  const canViewSensitiveInfo = (): boolean => {
    return hasPermission('view_sensitive_info');
  };

  // 检查是否可以查看财务信息
  const canViewFinancialInfo = (): boolean => {
    return hasPermission('view_financial_info');
  };

  // 检查是否可以查看统计信息
  const canViewStatistics = (): boolean => {
    return hasPermission('view_statistics');
  };

  // 获取统计信息级别
  const getStatisticsLevel = (): PermissionLevel => {
    return permissions?.statistics_level || 'none';
  };

  // 检查是否是管理员
  const isAdmin = (): boolean => {
    return permissions?.role === 'admin';
  };

  // 检查是否是总部员工
  const isHQStaff = (): boolean => {
    return permissions?.is_hq_staff || false;
  };

  // 检查是否是仓库主管
  const isWarehouseSupervisor = (): boolean => {
    return permissions?.is_warehouse_supervisor || false;
  };

  // 检查是否是仓库员工
  const isWarehouseStaff = (): boolean => {
    return permissions?.is_warehouse_staff || false;
  };

  // 检查是否是主账号
  const isMainAccount = (): boolean => {
    return permissions?.is_main_account || false;
  };

  // 检查是否是子账号
  const isSubAccount = (): boolean => {
    return permissions?.is_sub_account || false;
  };

  // 初始化时获取权限信息
  useEffect(() => {
    fetchPermissions();
  }, []);

  const contextValue: PermissionContextType = {
    permissions,
    loading,
    error,
    hasPermission,
    canViewSensitiveInfo,
    canViewFinancialInfo,
    canViewStatistics,
    getStatisticsLevel,
    refreshPermissions,
    isAdmin,
    isHQStaff,
    isWarehouseSupervisor,
    isWarehouseStaff,
    isMainAccount,
    isSubAccount,
    hiddenFields: (resourceKey: string) => permissions?.hidden_fields_by_resource?.[resourceKey] || [],
    exportHiddenFields: (resourceKey: string) => permissions?.export_hidden_fields_by_resource?.[resourceKey] || [],
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// 使用权限上下文的Hook
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionProvider;
